/**
 * Directives — per-state re-authorization rules (admin surface).
 *
 * Separate from `directivesApi.js` on purpose: that file is the clinical
 * surface (records, sign-off, catalog), this one is organization configuration.
 * Different audience, different privilege (61 `directive_policy_manage`,
 * not 54 `directive_approve_reauthorize`).
 *
 * Contract mirrors the migration:
 *   db/migrations/mysql/20260807212406_schema_directive_reauth_policy.sql
 *
 * The table is SPARSE — a state with no row requires no re-authorization at
 * all. An empty list is a valid answer meaning "no state we operate in imposes
 * a rule", never an error or a not-yet-loaded state.
 */

import axios from "axios";
import { CURR_API } from "@/constants/apis";

const WEBHOOK = `${CURR_API}/tswebhook.php`;

/** Mirrors ck_drp_reauth_days in the migration. Validated server-side too. */
export const REAUTH_DAYS_MIN = 1;
export const REAUTH_DAYS_MAX = 3650;

async function post(node) {
  const res = await axios.post(
    WEBHOOK,
    { DirectivePolicy: node },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
}

/** All rules for the account, ordered by state code. */
export async function getDirectiveReauthPolicies({ accountnumber, actorId }) {
  return post({
    API_Meth: "GetDirectiveReauthPolicies",
    accountnumber,
    actor_id: actorId,
  });
}

/**
 * How many ACTIVE records a save or a delete would touch, for the confirmation.
 *
 * Returns `{ awaiting, holding }`:
 *   awaiting — signed records in this state with NO deadline. A save gives each
 *              of them one, dated from today.
 *   holding  — records already carrying a deadline. A save leaves these alone
 *              (a changed window lands at their next re-authorization); a
 *              delete clears every one of them.
 */
export async function getDirectiveReauthPolicyImpact({ accountnumber, actorId, stateCode }) {
  return post({
    API_Meth: "GetDirectiveReauthPolicyImpact",
    accountnumber,
    actor_id: actorId,
    state_code: stateCode,
  });
}

/** Upsert one state's rule. `stateCode` is normalized server-side. */
export async function saveDirectiveReauthPolicy({
  accountnumber,
  actorId,
  actorName,
  stateCode,
  reauthDays,
}) {
  return post({
    API_Meth: "SaveDirectiveReauthPolicy",
    accountnumber,
    actor_id: actorId,
    actor_name: actorName,
    state_code: stateCode,
    reauth_days: reauthDays,
  });
}

/**
 * Remove a state's rule — i.e. that state no longer requires re-authorization.
 *
 * `actorName` is recorded on the retained row: removal is a soft delete, so the
 * rule keeps its window and its last actor for the audit trail rather than
 * vanishing. Without it the row would still carry whoever last *edited* it.
 */
export async function deleteDirectiveReauthPolicy({
  accountnumber,
  actorId,
  actorName,
  stateCode,
}) {
  return post({
    API_Meth: "DeleteDirectiveReauthPolicy",
    accountnumber,
    actor_id: actorId,
    actor_name: actorName,
    state_code: stateCode,
  });
}
